import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { saveAs } from 'file-saver';

/**
 * Exporta datos a Excel
 */
export const exportToExcel = (data, fileName = 'export.xlsx') => {
  const workbook = XLSX.utils.book_new();
  
  // Agregar hojas según el tipo de datos
  if (data.kpis) {
    const kpisSheet = XLSX.utils.json_to_sheet([
      {
        'Metric': 'Annual Revenue',
        'Value': data.kpis.revenue || 0,
        'Unit': '€'
      },
      {
        'Metric': 'Annual Expenses',
        'Value': data.kpis.expenses || 0,
        'Unit': '€'
      },
      {
        'Metric': 'Net Profit',
        'Value': data.kpis.profit || 0,
        'Unit': '€'
      }
    ]);
    XLSX.utils.book_append_sheet(workbook, kpisSheet, 'Summary');
  }

  if (data.partners && Array.isArray(data.partners)) {
    const partnersData = data.partners.map(p => ({
      'Company': p.partner_name,
      'Revenue': p.revenue || 0,
      'Type': p.is_customer ? 'Customer' : 'Supplier'
    }));
    const partnersSheet = XLSX.utils.json_to_sheet(partnersData);
    XLSX.utils.book_append_sheet(workbook, partnersSheet, 'Companies');
  }

  if (data.categories && Array.isArray(data.categories)) {
    const categoriesData = data.categories.map(c => ({
      'Category': c.category_name,
      'Revenue': c.revenue || 0,
      'Contracts': c.contracts ? c.contracts.length : 0
    }));
    const categoriesSheet = XLSX.utils.json_to_sheet(categoriesData);
    XLSX.utils.book_append_sheet(workbook, categoriesSheet, 'Categories');
  }

  if (data.monthly && Array.isArray(data.monthly)) {
    const monthlyData = data.monthly.map(m => ({
      'Month': m.month,
      'Revenue': m.revenue || 0,
      'Expenses': m.expenses || 0,
      'Net': (m.revenue || 0) - (m.expenses || 0)
    }));
    const monthlySheet = XLSX.utils.json_to_sheet(monthlyData);
    XLSX.utils.book_append_sheet(workbook, monthlySheet, 'Monthly');
  }

  if (data.contracts && Array.isArray(data.contracts)) {
    const contractsData = data.contracts.map(c => ({
      'Contract': c.contract_name,
      'Partner': c.partner_name,
      'Value': c.price || 0,
      'Type': c.is_customer ? 'Customer' : 'Supplier',
      'Recurrence': c.recurring_rule_type || 'N/A'
    }));
    const contractsSheet = XLSX.utils.json_to_sheet(contractsData);
    XLSX.utils.book_append_sheet(workbook, contractsSheet, 'Contracts');
  }

  XLSX.writeFile(workbook, fileName);
};

/**
 * Exporta un elemento HTML a PDF
 */
export const exportToPDF = async (elementId, fileName = 'export.pdf') => {
  try {
    const element = document.getElementById(elementId);
    if (!element) {
      console.error(`Element with id '${elementId}' not found`);
      return;
    }

    // Capturar el elemento como canvas
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff'
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const imgWidth = pdf.internal.pageSize.getWidth() - 20;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let yPosition = 10;

    pdf.addImage(imgData, 'PNG', 10, yPosition, imgWidth, imgHeight);

    // Agregar más páginas si es necesario
    let heightLeft = imgHeight - (pdf.internal.pageSize.getHeight() - 20);
    let position = 0;

    while (heightLeft >= 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 10, position + 10, imgWidth, imgHeight);
      heightLeft -= pdf.internal.pageSize.getHeight();
    }

    pdf.save(fileName);
  } catch (error) {
    console.error('Error exporting to PDF:', error);
  }
};

/**
 * Exporta múltiples gráficos a PDF
 */
export const exportChartsToPDF = async (chartIds, fileName = 'charts.pdf', title = 'Dashboard Report') => {
  try {
    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    let isFirstPage = true;
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    // Agregar título
    pdf.setFontSize(16);
    pdf.text(title, 10, 15);
    pdf.setFontSize(10);
    pdf.text(`Generated on: ${new Date().toLocaleString()}`, 10, 22);

    let yPosition = 30;

    for (const chartId of chartIds) {
      const element = document.getElementById(chartId);
      if (!element) continue;

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas.toDataURL('image/png');
      const imgWidth = pageWidth - 20;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Si el gráfico no cabe en la página actual, crear una nueva
      if (yPosition + imgHeight > pageHeight - 10) {
        if (!isFirstPage) {
          pdf.addPage();
        }
        isFirstPage = false;
        yPosition = 10;
      }

      pdf.addImage(imgData, 'PNG', 10, yPosition, imgWidth, imgHeight);
      yPosition += imgHeight + 10;
    }

    pdf.save(fileName);
  } catch (error) {
    console.error('Error exporting charts to PDF:', error);
  }
};

/**
 * Exporta datos como CSV
 */
export const exportToCSV = (data, fileName = 'export.csv') => {
  let csv = '';

  if (Array.isArray(data)) {
    if (data.length === 0) return;

    // Headers
    const headers = Object.keys(data[0]);
    csv = headers.join(',') + '\n';

    // Data
    data.forEach(row => {
      csv += headers.map(header => {
        const value = row[header];
        if (typeof value === 'string' && value.includes(',')) {
          return `"${value}"`;
        }
        return value;
      }).join(',') + '\n';
    });
  }

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  saveAs(blob, fileName);
};

export default {
  exportToExcel,
  exportToPDF,
  exportChartsToPDF,
  exportToCSV
};
